/**
 * SearchInput Component
 *
 * Debounced search input component for filtering referrals.
 * Features real-time search with debouncing, loading states,
 * clear functionality, and accessibility support.
 *
 * @module components/provider-dashboard/SearchInput
 */

import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/Input';
import { useTheme } from '@/hooks/useTheme';
import type { SearchInputProps } from '@/types/dashboard';
import { cn } from '@/utils/cn';

/**
 * Search input component with debouncing and clear functionality
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder = 'Search referrals...',
  debounceMs = 300,
  className,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const { getTextColorClass, getThemeClasses } = useTheme();

  // Update local value when external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounce the onChange callback
  useEffect(() => {
    if (localValue !== value) {
      setIsDebouncing(true);

      const timeoutId = setTimeout(() => {
        onChange(localValue);
        setIsDebouncing(false);
      }, debounceMs);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      setIsDebouncing(false);
    }
  }, [localValue, value, onChange, debounceMs]);

  /**
   * Handle input change
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
  };

  /**
   * Clear search input
   */
  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Clear on Escape key
    if (event.key === 'Escape') {
      handleClear();
      event.preventDefault();
    }
  };

  // Routed through Input's own leftIcon/rightIcon/loading slots so the input
  // reserves left/right gutters (pl-10/pr-10) and the icons sit above the input's
  // background layer. A plain sibling icon is painted over by that layer, and
  // there is no `ph-*` utility to reserve the gutter.
  const searchIcon = (
    <span className={cn('text-lg', getTextColorClass('muted'))} aria-hidden="true">
      🔍
    </span>
  );

  const clearButton = localValue ? (
    <button
      type="button"
      onClick={handleClear}
      className={cn(
        getTextColorClass('muted'),
        getThemeClasses(
          'hover:text-gray-600 focus:text-gray-600',
          'hover:text-gray-300 focus:text-gray-300'
        ),
        'focus:outline-none transition-colors duration-150'
      )}
      aria-label="Clear search"
    >
      <span className="text-lg" aria-hidden="true">✕</span>
    </button>
  ) : undefined;

  return (
    <div className={cn('relative', className)}>
      <Input
        type="text"
        value={localValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        leftIcon={searchIcon}
        loading={isDebouncing}
        rightIcon={clearButton}
        className={cn({
          [getThemeClasses('border-blue-300', 'border-blue-600')]: isDebouncing,
        })}
        aria-label="Search referrals"
        aria-describedby="search-help"
      />

      {/* Screen reader help text */}
      <div id="search-help" className="sr-only">
        Search through referrals by patient name, condition, or Accredited Practicing Dietitian.
        Press Escape to clear the search.
      </div>
    </div>
  );
};

export default SearchInput;